import { Injectable } from '@angular/core';
import { PackagingManagerService } from '@app/services/api/packaging-manager.service';
import { FlashService } from '@app/services/flash.service';
import {
  AddAllAssigned,
  AddAllPackagingUsers,
  AddAllUnassigned,
  AssignUser,
  AssignUserFail,
  AssignUserSuccess,
  LoadAssigned,
  LoadAssignedFail,
  LoadAssignedSuccess,
  LoadPackagingUsers,
  LoadPackagingUsersFail,
  LoadPackagingUsersSuccess,
  LoadUnassigned,
  LoadUnassignedFail,
  LoadUnassignedSuccess,
  PackagingManagerActionTypes,
  UnassignUser,
  UnassignUserFail,
  UnassignUserSuccess
} from '@core/actions/packaging-manager.actions';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable()
export class PackagingManagerEffects {
  constructor(
    private actions$: Actions,
    private packagingManagersApi: PackagingManagerService,
    private fs: FlashService
  ) {}

  @Effect()
  loadUnassigned$ = this.actions$.pipe(
    ofType(PackagingManagerActionTypes.LoadUnassigned),
    switchMap((action: LoadUnassigned) =>
      this.packagingManagersApi.getUnassigned(action.filters).pipe(
        map(
          packagingManagers =>
            new LoadUnassignedSuccess(packagingManagers)
        ),
        catchError(error => of(new LoadUnassignedFail(error)))
      )
    )
  );

  @Effect()
  loadUnassignedSuccess$ = this.actions$.pipe(
    ofType<LoadUnassignedSuccess>(
      PackagingManagerActionTypes.LoadUnassignedSuccess
    ),
    map(action => new AddAllUnassigned(action.packagingManagers))
  );

  @Effect()
  loadAssigned$ = this.actions$.pipe(
    ofType(PackagingManagerActionTypes.LoadAssigned),
    switchMap((action: LoadAssigned) =>
      this.packagingManagersApi.getAssigned(action.filters).pipe(
        map(
          packagingManagers => new LoadAssignedSuccess(packagingManagers)
        ),
        catchError(error => of(new LoadAssignedFail(error)))
      )
    )
  );

  @Effect()
  loadAssignedSuccess$ = this.actions$.pipe(
    ofType<LoadAssignedSuccess>(PackagingManagerActionTypes.LoadAssignedSuccess),
    map(action => new AddAllAssigned(action.packagingManagers))
  );

  @Effect()
  loadPackagingUsers$ = this.actions$.pipe(
    ofType(PackagingManagerActionTypes.LoadPackagingUsers),
    switchMap(() =>
      this.packagingManagersApi.getPackagingUsers().pipe(
        map(packagingUsers => new LoadPackagingUsersSuccess(packagingUsers)),
        catchError(error => of(new LoadPackagingUsersFail(error)))
      )
    )
  );

  @Effect()
  loadPackagingUsersSuccess$ = this.actions$.pipe(
    ofType<LoadPackagingUsersSuccess>(
      PackagingManagerActionTypes.LoadPackagingUsersSuccess
    ),
    map(action => new AddAllPackagingUsers(action.packagingUsers))
  );

  @Effect()
  assignUser$ = this.actions$.pipe(
    ofType<AssignUser>(PackagingManagerActionTypes.AssignUser),
    switchMap(action =>
      this.packagingManagersApi
        .assignUser(action.packagingId, action.userId)
        .pipe(
          map(packaging => new AssignUserSuccess(packaging)),
          catchError(error => of(new AssignUserFail(error)))
        )
    )
  );

  @Effect({ dispatch: false })
  assignUserSuccess$ = this.actions$.pipe(
    ofType<AssignUserSuccess>(PackagingManagerActionTypes.AssignUserSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Usuario asignado' });
    })
  );

  @Effect({ dispatch: false })
  assignUserFail$ = this.actions$.pipe(
    ofType<AssignUserFail>(PackagingManagerActionTypes.AssignUserFail),
    map(action => {
      this.fs.emit({ type: 'danger', message: 'No se pudo asignar el usuario' });
    })
  );

  @Effect()
  unassignUser$ = this.actions$.pipe(
    ofType<UnassignUser>(PackagingManagerActionTypes.UnassignUser),
    switchMap(action =>
      this.packagingManagersApi
        .unassignUser(action.packagingId, action.userId)
        .pipe(
          map(() => new UnassignUserSuccess(action.packagingId, action.userId)),
          catchError(error => of(new UnassignUserFail(error)))
        )
    )
  );

  @Effect({ dispatch: false })
  unassignUserSuccess$ = this.actions$.pipe(
    ofType<UnassignUserSuccess>(
      PackagingManagerActionTypes.UnassignUserSuccess
    ),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Usuario desasignado' });
    })
  );

  @Effect({ dispatch: false })
  unassignUserFail$ = this.actions$.pipe(
    ofType<UnassignUserFail>(PackagingManagerActionTypes.UnassignUserFail),
    map(action => {
      this.fs.emit({
        type: 'danger',
        message: 'No se pudo desasignar el usuario'
      });
    })
  );
}
